import { useTranslate } from "@growtherapy/i18n";
import { Mutex } from "async-mutex";
import { useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback, useEffect, useMemo } from "react";
import { mapValues } from "remeda";
import { useDebounceCallback } from "usehooks-ts";
import { sendLoggingEvents, TrackingEvents } from "../../events";
import { toast } from "../../toasts";
import type { RoomEvent, RoomEventHandlerMap } from "../../video-sdk";
import {
  parseIdentity,
  remoteParticipantToDiagnosticInfo,
  roomAtom,
} from "../../video-sdk";
import { dominantSpeakerIdAtom } from "../../video-sdk/twilio";
import { remoteParticipantConnectionStatesByIdAtom } from "../../video-sdk/core/room/participant/atoms";
import { ParticipantConnectionState } from "../../video-sdk/core/room/participant/types";
import { logger } from "../../datadog/logger";
import {
  ParticipantConnectedCallbackOpts,
  useParticipantConnectedEventHandlerCallback,
} from "./useParticipantConnectedEventHandlerCallback";
import { useIsRejoinEnabled } from "../session-grid/reconnect/useIsRejoinEnabled";
import { useDisconnectedEventHandlerCallback } from "./useDisconnectedEventHandlerCallback";
import { useSyncConnectedParticipantsCallback } from "./useSyncConnectedParticipantsCallback";
import { useParticipantDisconnectedEventHandler } from "./useParticipantDisconnectedEventHandler";

const RECONNECT_TOAST_KEY = "auto-reconnect-toast";

type RoomDisconnectedEventHandler = RoomEventHandlerMap["disconnected"];
type RoomParticipantDisconnectedEventHandler =
  RoomEventHandlerMap["participantDisconnected"];
type RoomReconnectingEventHandler = RoomEventHandlerMap["reconnecting"];

type UseRoomProps = {
  onRoomDisconnect?: RoomDisconnectedEventHandler;
  onParticipantDisconnect?: RoomParticipantDisconnectedEventHandler;
  /**
   * Called only when a participant first connects
   */
  onParticipantInitialConnect?: ParticipantConnectedCallbackOpts["onParticipantInitialConnect"];
  onPageUnload?: () => void;
};

export const RAPID_RECONNECT_THRESHOLD = 1000;
export const CHECK_FOR_EXISTING_PARTICIPANTS_INTERVAL = 3000;

export const roomEventListenerMutex = new Mutex();

/**
 * Wraps the given event handlers in the given mutex, ensuring that only one
 * handler can run at a time.
 */
export function mutexifyEventHandlers(
  handlers: RoomEventHandlerMap,
): RoomEventHandlerMap {
  return mapValues(
    handlers,
    (handler) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      async (...args: any[]) =>
        roomEventListenerMutex.runExclusive(() => handler(...args)),
  );
}

type UseGetRoomEventListenersProps = Omit<UseRoomProps, "onPageUnload"> & {
  onClearReconnectionNotification?: () => void;
  onNotifyOfReconnection?: (error: Error) => void;
};

function useGetRoomEventListeners({
  onClearReconnectionNotification,
  onNotifyOfReconnection,
  onParticipantInitialConnect,
  onParticipantDisconnect,
  onRoomDisconnect,
}: UseGetRoomEventListenersProps = {}): RoomEventHandlerMap {
  const setDominantSpeakerId = useSetAtom(dominantSpeakerIdAtom);
  const handleDisconnectedEvent = useDisconnectedEventHandlerCallback();

  const setRemoteParticipantConnectionStatesById = useSetAtom(
    remoteParticipantConnectionStatesByIdAtom,
  );

  const handleParticipantConnected =
    useParticipantConnectedEventHandlerCallback();
  const handleParticipantDisconnected =
    useParticipantDisconnectedEventHandler();

  const [_, checkRejoinIsEnabled] = useIsRejoinEnabled();

  return useMemo<RoomEventHandlerMap>(
    () =>
      mutexifyEventHandlers({
        participantReconnecting: async (participant) => {
          if (!checkRejoinIsEnabled()) {
            return;
          }
          sendLoggingEvents(
            TrackingEvents.REMOTE_PARTICIPANT_RECONNECTING,
            {
              participant: remoteParticipantToDiagnosticInfo(participant),
            },
            {
              logLevel: "info",
              message: "Remote participant reconnecting",
            },
          );
          setRemoteParticipantConnectionStatesById((prev) => ({
            ...prev,
            [parseIdentity(participant.identity).id]:
              ParticipantConnectionState.Reconnecting,
          }));
        },
        participantReconnected: async (participant) => {
          if (!checkRejoinIsEnabled()) {
            return;
          }
          handleParticipantConnected(participant, { isUpdate: true });
        },
        participantConnected: async (participant) =>
          handleParticipantConnected(participant, {
            isUpdate: false,
            onParticipantInitialConnect,
          }),
        participantDisconnected: async (participant) => {
          await handleParticipantDisconnected(participant, {
            onParticipantDisconnect,
          });
        },
        disconnected: async (room, error, eventOptions) => {
          await handleDisconnectedEvent(room, error, {
            ...eventOptions,
            onTeardownRoomCompleted: onRoomDisconnect,
          });
        },
        dominantSpeakerChanged: (participant) => {
          if (!participant) return;

          sendLoggingEvents(
            TrackingEvents.ROOM_DOMINANT_SPEAKER_CHANGED,
            {
              participant: remoteParticipantToDiagnosticInfo(participant),
            },
            { logLevel: "info", message: "Dominant speaker changed" },
          );
          setDominantSpeakerId(participant.identity);
        },
        reconnecting: (error) => {
          sendLoggingEvents(
            TrackingEvents.ROOM_RECONNECTING,
            {},
            { error, logLevel: "warn", message: "Reconnecting to room" },
          );
          onNotifyOfReconnection?.(error);
        },
        reconnected: () => {
          sendLoggingEvents(
            TrackingEvents.ROOM_RECONNECTED,
            {},
            { logLevel: "info", message: "Reconnected to room" },
          );
          onClearReconnectionNotification?.();
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      onClearReconnectionNotification,
      onNotifyOfReconnection,
      onParticipantDisconnect,
      onRoomDisconnect,
    ],
  );
}

export function useRoomEventListeners({
  onPageUnload,
  ...roomEventCallbacks
}: UseRoomProps = {}) {
  const t = useTranslate();
  const room = useAtomValue(roomAtom);
  const getRoom = useAtomCallback(useCallback((get) => get(roomAtom), []));
  const syncConnectedParticipants = useSyncConnectedParticipantsCallback();
  const onNotifyOfReconnection = useDebounceCallback(
    useCallback<RoomReconnectingEventHandler>(() => {
      toast.error(t("telehealth-core:errorsCore.wereTryingToReconnectYou"), {
        timeout: Infinity,
        key: RECONNECT_TOAST_KEY,
        purpose: "system",
      });
    }, [t]),
    RAPID_RECONNECT_THRESHOLD,
  );
  const onClearReconnectionNotification = useCallback(() => {
    // if reconnected before the notification, cancel the delayed notification
    // (this is ignored if it has already happened)
    onNotifyOfReconnection.cancel();

    toast.close(RECONNECT_TOAST_KEY);
  }, [onNotifyOfReconnection]);
  const roomEventListeners = useGetRoomEventListeners({
    ...roomEventCallbacks,
    onClearReconnectionNotification,
    onNotifyOfReconnection,
  });

  useEffect(
    function handleRoomEventListeners() {
      if (!room) {
        onClearReconnectionNotification();
        return;
      }

      const detachEventListenerCallbacks = Object.entries(
        roomEventListeners,
      ).map(([event, callback]) => {
        return room.on(event as RoomEvent, callback);
      });

      syncConnectedParticipants();

      return function cleanup() {
        for (const callback of detachEventListenerCallbacks) {
          callback();
        }
      };
    },
    [
      room,
      onClearReconnectionNotification,
      roomEventListeners,
      syncConnectedParticipants,
    ],
  );

  useEffect(
    /**
     * For resiliency, periodically check for existing participants and handle
     * them.
     */
    function checkForExistingParticipantsPeriodically() {
      if (!room) return;

      const interval = setInterval(() => {
        const syncedParticipants = syncConnectedParticipants();
        if (syncedParticipants.length > 0) {
          // Let's check for the frequency of these logs and remove this
          // resiliency effect if it's not happening
          logger.warn("Stale participants detected in app state", {
            participants: syncedParticipants.map(
              remoteParticipantToDiagnosticInfo,
            ),
          });
        }
      }, CHECK_FOR_EXISTING_PARTICIPANTS_INTERVAL);

      return function cleanup() {
        clearInterval(interval);
      };
    },
    [room, syncConnectedParticipants],
  );

  useEffect(
    function attemptDisconnectBeforePageUnload() {
      const abortController = new AbortController();
      window.addEventListener(
        "beforeunload",
        () => {
          // Callback increase chance of successfully disconnecting before the
          // tab is unloaded
          getRoom()?.disconnect();
          onPageUnload?.();
        },
        { signal: abortController.signal },
      );
      return () => abortController.abort();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(
    function cleanUpTimersAndNotificationsOnUnmount() {
      // on unmount, clear any notifications behavior
      return onClearReconnectionNotification;
    },
    [onClearReconnectionNotification],
  );

  return room;
}
